import type { TranslateOptions, Locale } from "vue-i18n";
import { computed } from "vue";
// eslint-disable-next-line no-restricted-imports
import { useI18n } from "vue-i18n";
import type ILocaleKeys from "@/i18n/locales/en.json";
import { DEFAULT_LOCALE } from "@/domain/constants";
import { useCookie, useHeadSafe } from "#imports";

/** TODO: move to a @chatfood/ts-toolbelt-ish package */
type SafeKey<T> = T extends string | number ? `${T}` : never;
type Paths<O, P = ""> = O extends object
  ? {
      [K in keyof O]:
        | (P extends ""
            ? O[K] extends object
              ? never
              : K
            : `${SafeKey<P>}.${SafeKey<K>}`)
        | Paths<O[K], P extends "" ? K : `${SafeKey<P>}.${SafeKey<K>}`>;
    }[keyof O]
  : never;

export type ILocaleKeypath = Paths<typeof ILocaleKeys>;

export const useLocale = () => {
  const {
    t: vueT,
    te: vueTe,
    locale: vueLocale,
    setLocale: vueSetLocale,
    setLocaleCookie,
    availableLocales: vueAvailableLocales,
  } = useI18n();

  const t = (
    path: ILocaleKeypath,
    params: Record<string, string | number> = {},
    options?: TranslateOptions,
  ) => {
    return vueT(path, params, options ?? {});
  };

  const hasTranslation = (path: string): path is ILocaleKeypath => {
    return vueTe(path) || vueTe(path, DEFAULT_LOCALE);
  };

  const extractTranslations = async (basePath: string) => {
    const loadLocale = async (locale: string) => {
      const localeModule = await import(`@/i18n/locales/${locale}.json?raw`);
      const data = JSON.parse(localeModule.default);
      return (
        basePath.split(".").reduce((current, key) => current?.[key], data) || {}
      );
    };

    try {
      return await loadLocale(vueLocale.value);
    } catch {
      return await loadLocale(DEFAULT_LOCALE);
    }
  };

  const setLocale = async (locale: Locale) => {
    useHeadSafe({
      htmlAttrs: {
        lang: locale,
        dir: locale.startsWith("ar") ? "rtl" : "ltr",
      },
    });

    return await vueSetLocale(locale as Locale);
  };

  const availableLocales = computed<Locale[]>(() => {
    return vueAvailableLocales as Locale[];
  });

  const locale = vueLocale;

  const isLocale = (lang: string): lang is Locale => {
    return (availableLocales.value as readonly string[]).includes(lang);
  };

  const setDefaultLanguage = (
    language: string,
    acceptedLanguages: string[],
  ): void => {
    const supportedLangs = [...acceptedLanguages, language];
    const cookieLang = useCookie("i18n_redirected").value;

    if (
      cookieLang &&
      isLocale(cookieLang) &&
      supportedLangs.includes(cookieLang)
    ) {
      setLocale(cookieLang);
      return;
    }

    if (isLocale(language) && supportedLangs.includes(language)) {
      setLocale(language);
      return;
    }

    setLocale("en");
  };

  return {
    t,
    hasTranslation,
    extractTranslations,
    locale,
    setLocale,
    setLocaleCookie,
    setDefaultLanguage,
    isLocale,
    availableLocales,
  };
};
